import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmationDialog from '@sayyesschool/frontend/components/confirmation-dialog';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { useSection } from '@sayyesschool/frontend/features/courses';
import { useExercise } from '@sayyesschool/frontend/features/exercises';
import { useBoolean } from '@sayyesschool/frontend/hooks/state';
import { Flex, Grid } from '@sayyesschool/frontend/ui-components';

import ExerciseDescription from '../exercise-description';
import ExerciseItems from '../exercise-items';
import ExerciseNotes from '../exercise-notes';

export default function ExercisePage() {
    const navigate = useNavigate();
    const params = useParams();

    const [exercise, exerciseActions] = useExercise(params.exerciseId);
    const { course, unit, lesson, section } = useSection({ courseId: params.courseId, sectionId: exercise?.sectionId });
    const [isConfirmationDialogOpen, toggleConfirmationDialogOpen] = useBoolean(false);

    const handleUpdate = useCallback(data => {
        return exerciseActions.updateExercise(exercise.id, data);
    }, [exercise, exerciseActions]);

    const handleDelete = useCallback(() => {
        return exerciseActions.deleteExercise(exercise.id, course.id)
            .then(() => navigate(lesson.url));
    }, [course, lesson, exercise, exerciseActions, navigate]);

    const handleCreateItem = useCallback(data => {
        return exerciseActions.createExerciseItem(exercise.id, data);
    }, [exercise, exerciseActions]);

    const handleUpdateItem = useCallback((itemId, data) => {
        return exerciseActions.updateExerciseItem(exercise.id, itemId, data);
    }, [exercise, exerciseActions]);

    const handleDeleteItem = useCallback((itemId, data) => {
        return exerciseActions.deleteExerciseItem(exercise.id, itemId, data);
    }, [exercise, exerciseActions]);

    if (!course || !exercise) return <LoadingIndicator fluid />;

    return (
        <Page className="ExercisePage">
            <Page.Header
                title="Упражнение"
                description={`ID: ${exercise.id}`}
                breadcrumbs={[
                    { key: 'course', content: course.title, to: course.uri, title: 'Курс' },
                    { key: 'unit', content: unit.title, to: unit.uri, title: 'Юнит' },
                    { key: 'lesson', content: lesson.title, to: lesson.uri, title: 'Урок' },
                    { key: 'section', content: section.title, to: section.uri, title: 'Секция' }
                ]}
                actions={[
                    {
                        key: 'delete',
                        icon: 'delete',
                        title: 'Удалить упражнение',
                        onClick: toggleConfirmationDialogOpen
                    }
                ]}
            />

            <Page.Content>
                <Grid spacing={2}>
                    <Grid.Item xs={8}>
                        <Flex
                            gap="medium"
                            column
                        >
                            <ExerciseDescription
                                exercise={exercise}
                                onUpdate={handleUpdate}
                            />

                            <ExerciseItems
                                exercise={exercise}
                                onCreate={handleCreateItem}
                                onUpdate={handleUpdateItem}
                                onDelete={handleDeleteItem}
                                onReorder={handleUpdate}
                            />
                        </Flex>
                    </Grid.Item>

                    <Grid.Item xs={4}>
                        <ExerciseNotes
                            exercise={exercise}
                            onUpdate={handleUpdate}
                        />
                    </Grid.Item>
                </Grid>
            </Page.Content>

            <ConfirmationDialog
                title="Удалить упражнение?"
                message="Упражнение будет удален без возможности восстановления."
                open={isConfirmationDialogOpen}
                onClose={toggleConfirmationDialogOpen}
                onConfirm={handleDelete}
            />
        </Page>
    );
}